import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Check, X } from "lucide-react";
import { LogoIcon } from "../../../assets/icon";
import useRequest from "../../../components/hooks/use-req";
import { formatCurrency } from "../../../utils/function";

// Standalone landing page Flutterwave redirects back to after an advertiser funds their
// wallet (see `handleConfirmAddFunds` in `pages/advertiser/wallet/index.tsx`, which sends
// the browser to the `payment_link` returned by `/dashboard/advertiser/wallet/add-funds`).
// The redirect URL looks like:
//   /wallet/subscription?status=successful&tx_ref=...&transaction_id=...
// Confirmed verify payload (posted to `/dashboard/advertiser/wallet/add-funds/verify`):
//   { tx_ref: string, transaction_id: string }
const WalletSubscription: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const authToken = localStorage.getItem("authToken");

  const [status, setStatus] = useState<"verifying" | "success" | "failed">("verifying");
  const [amount, setAmount] = useState<number>(0);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const { makeRequest: verifyFunding } = useRequest(
    "/dashboard/advertiser/wallet/add-funds/verify",
    "POST",
    { Authorization: authToken ? `Bearer ${authToken}` : undefined }
  );

  useEffect(() => {
    const txRef = searchParams.get("tx_ref") || "";
    const transactionId = searchParams.get("transaction_id") || "";
    const gatewayStatus = searchParams.get("status") || "";

    // Flutterwave's redirect doesn't carry the amount, and the verify endpoint hasn't been
    // returning it either — fall back to what we stashed right before leaving for checkout
    // (see `handleConfirmAddFunds` in `pages/advertiser/wallet/index.tsx`), matched by tx_ref
    // so a stale/unrelated entry from an earlier attempt never gets shown by mistake.
    let pendingAmount = 0;
    try {
      const pending = JSON.parse(localStorage.getItem("pendingWalletTopup") || "null");
      if (pending?.tx_ref && pending.tx_ref === txRef) {
        pendingAmount = Number(pending.amount) || 0;
      }
    } catch {
      // malformed localStorage value — ignore, just no fallback amount
    }

    const verify = async () => {
      if (!txRef || !transactionId) {
        setErrorMessage("We couldn't find your payment reference.");
        setStatus("failed");
        return;
      }

      // Flutterwave marks explicitly cancelled/failed attempts on the URL itself — no need
      // to round-trip to the backend for those, the tx_ref was never actually paid.
      if (gatewayStatus && gatewayStatus !== "successful" && gatewayStatus !== "completed") {
        setErrorMessage("This payment was not completed.");
        setStatus("failed");
        localStorage.removeItem("pendingWalletTopup");
        return;
      }

      const [response, error] = await verifyFunding({ tx_ref: txRef, transaction_id: transactionId });

      const verifiedAmount =
        response?.amount ??
        error?.amount ??
        response?.data?.amount ??
        response?.data?.amount_funded ??
        pendingAmount;

      const isSuccess =
        response?.status === true ||
        response?.data?.status === "success" ||
        response?.data?.status === "successful" ||
        response?.success === true;

      if (isSuccess) {
        setAmount(verifiedAmount || 0);
        setStatus("success");
      } else {
        setErrorMessage(response?.message || error?.message || "We couldn't verify this payment.");
        setStatus("failed");
      }

      localStorage.removeItem("pendingWalletTopup");
    };

    verify();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleBack = () => navigate("/business/wallet");

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-gradient-to-b from-[#EAF8F1] to-white px-4">
      <div className="bg-white w-full max-w-md rounded-3xl shadow-xl p-8 sm:p-10 text-center">
        <div className="flex justify-center mb-8">
          <LogoIcon />
        </div>

        {status === "verifying" && (
          <>
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-emerald-500 mx-auto mb-6" />
            <h1 className="text-lg font-bold text-slate-900">Verifying your payment...</h1>
            <p className="text-sm text-slate-500 mt-2">This will only take a moment.</p>
          </>
        )}

        {status === "success" && (
          <>
            <h1 className="text-lg font-bold text-slate-900">
              {amount > 0
                ? `${formatCurrency(amount, "NGN", "symbol")} has been added to your advertiser wallet.`
                : "Your payment has been added to your advertiser wallet."}
            </h1>
            <p className="text-sm text-slate-500 mt-2">
              Funds have been added successfully and will be used to power active reward programs across VouchLinks.
            </p>

            <div
              className="w-20 h-20 rounded-full mx-auto my-8 p-[3px]"
              style={{ background: "linear-gradient(135deg, #00A86B 0%, #4F46E5 100%)" }}
            >
              <div className="w-full h-full rounded-full bg-white flex items-center justify-center">
                <Check className="text-blue-500" size={32} strokeWidth={3} />
              </div>
            </div>
          </>
        )}

        {status === "failed" && (
          <>
            <h1 className="text-lg font-bold text-slate-900">We couldn't confirm your payment.</h1>
            <p className="text-sm text-slate-500 mt-2">{errorMessage} If you were charged, this should resolve shortly — otherwise, please try again.</p>

            <div className="w-20 h-20 rounded-full border-4 border-red-100 flex items-center justify-center mx-auto my-8">
              <X className="text-red-500" size={32} strokeWidth={3} />
            </div>
          </>
        )}

        <button
          onClick={handleBack}
          disabled={status === "verifying"}
          className="w-full bg-black text-white rounded-full py-3.5 text-sm font-semibold hover:bg-slate-900 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Back to VouchLinks
        </button>
      </div>
    </div>
  );
};

export default WalletSubscription;
